import { CompleteKundali, PlanetName, ZodiacSign } from '../types/kundali';
import { ZODIAC_SIGNS, ZODIAC_LORDS, getSavByBhava, BPHS_ASHTAKAVARGA_RULES } from './vedicEngine';

export interface PrastaraTable {
  planet: PlanetName;
  contributors: {
    [key in string]: number[]; // 12 signs array of 0 or 1
  };
  signTotals: number[];
}

export interface ShodhanaResult {
  planet: PlanetName;
  original: number[];
  trikonaReduced: number[];
  ekadhipatyaReduced: number[];
  shodhita: number[]; // Classical Shodhita Bindu array (post-Ekadhipatya reduction)
  rashiPinda: number;
  grahaPinda: number;
  shodhyaPinda: number;
}

export interface AshtakvargaPredictionItem {
  category: string;
  title: string;
  houseNumber: number;
  sign: ZodiacSign;
  savScore: number;
  bavScore: number;
  supportLevel: 'Strongly Supported' | 'Moderately Supported' | 'Requires Caution';
  confidenceLevel: number; // percentage e.g. 85%
  whyExplanation: {
    houseSav: string;
    houseLord: string;
    bavDetails: string;
    transitNote: string;
  };
}

/**
 * Calculates Prastara Ashtakvarga tables for all 7 planets (Sign-Indexed 0 = Aries ... 11 = Pisces)
 */
export function calculatePrastaraTables(kundali: CompleteKundali): Record<string, PrastaraTable> {
  const { planets } = kundali;
  const planetSignMap = new Map<string, number>();
  planets.forEach(p => planetSignMap.set(p.name, p.signIndex));
  
  // Ensure Ascendant is in planetSignMap
  const ascSign = kundali.houses[0]?.signIndex ?? (planetSignMap.get('Ascendant') ?? 0);
  planetSignMap.set('Ascendant', ascSign);

  const result: Record<string, PrastaraTable> = {};
  const bavPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  // Canonical Kakshya Order (slowest orbit to fastest + Lagna: Saturn, Jupiter, Mars, Sun, Venus, Mercury, Moon, Ascendant)
  const contributors = ['Saturn', 'Jupiter', 'Mars', 'Sun', 'Venus', 'Mercury', 'Moon', 'Ascendant'];

  const rules = BPHS_ASHTAKAVARGA_RULES;

  bavPlanets.forEach(target => {
    const contribMap: Record<string, number[]> = {};
    const signTotals = new Array(12).fill(0);

    contributors.forEach(c => {
      const cSign = planetSignMap.get(c) ?? 0;
      const arr = new Array(12).fill(0);
      const benefics = rules[target][c] || [];
      benefics.forEach(offset => {
        const targetSignIndex = (cSign + (offset - 1)) % 12;
        arr[targetSignIndex] = 1;
      });
      contribMap[c] = arr;
    });

    for (let s = 0; s < 12; s++) {
      signTotals[s] = contributors.reduce((sum, c) => sum + contribMap[c][s], 0);
    }

    result[target] = {
      planet: target,
      contributors: contribMap,
      signTotals
    };
  });

  return result;
}

/**
 * Computes Classical Parashari Trikona Shodhana, Ekadhipatya Shodhana, and Shodhya Pinda
 * for all 7 BAV planets (strictly Sign-Indexed: 0 = Aries ... 11 = Pisces).
 */
export function calculateShodhanaAndPindas(kundali: CompleteKundali): ShodhanaResult[] {
  const bavPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const results: ShodhanaResult[] = [];

  // Determine which signs are occupied by physical grahas (Sun to Ketu; excluding Ascendant)
  const occupiedSigns = new Set<number>();
  kundali.planets.forEach(p => {
    if (p.name !== 'Ascendant') {
      occupiedSigns.add(p.signIndex);
    }
  });

  bavPlanets.forEach(planet => {
    // Canonical raw BAV array (strictly Sign-Indexed 0..11)
    const rawBav = kundali.ashtakvarga.bySign?.bhinna?.[planet] || (kundali.ashtakvarga.bhinna as any)?.[planet];
    const original = [...(rawBav || new Array(12).fill(4))];
    
    // =========================================================================
    // 1. Classical Trikona Shodhana (Triplicity Reduction)
    // =========================================================================
    // Trines across the 4 elements: Fire (0,4,8), Earth (1,5,9), Air (2,6,10), Water (3,7,11)
    const trikonaReduced = [...original];
    const triads = [
      [0, 4, 8],  // Fire: Aries, Leo, Sagittarius
      [1, 5, 9],  // Earth: Taurus, Virgo, Capricorn
      [2, 6, 10], // Air: Gemini, Libra, Aquarius
      [3, 7, 11]  // Water: Cancer, Scorpio, Pisces
    ];

    triads.forEach(triad => {
      const vals = triad.map(idx => trikonaReduced[idx]);
      const minVal = Math.min(...vals);
      triad.forEach(idx => {
        trikonaReduced[idx] = Math.max(0, trikonaReduced[idx] - minVal);
      });
    });

    // =========================================================================
    // 2. Classical Ekadhipatya Shodhana (Dual Lordship Reduction - BPHS Ch. 67)
    // =========================================================================
    // Applies strictly to planets owning two signs: Mars, Venus, Mercury, Jupiter, Saturn.
    // Cancer (Moon) and Leo (Sun) have single lords and undergo zero Ekadhipatya reduction.
    const ekadhipatyaReduced = [...trikonaReduced];
    const lordshipPairs: { planet: PlanetName; signs: [number, number] }[] = [
      { planet: 'Mars', signs: [0, 7] },      // Aries & Scorpio
      { planet: 'Venus', signs: [1, 6] },     // Taurus & Libra
      { planet: 'Mercury', signs: [2, 5] },   // Gemini & Virgo
      { planet: 'Jupiter', signs: [8, 11] },  // Sagittarius & Pisces
      { planet: 'Saturn', signs: [9, 10] }    // Capricorn & Aquarius
    ];

    lordshipPairs.forEach(({ signs: [s1, s2] }) => {
      const v1 = ekadhipatyaReduced[s1];
      const v2 = ekadhipatyaReduced[s2];

      const occ1 = occupiedSigns.has(s1);
      const occ2 = occupiedSigns.has(s2);

      // Case A: Both signs are occupied -> No reduction
      if (occ1 && occ2) {
        return;
      }

      // Case B: Both signs are unoccupied (empty)
      if (!occ1 && !occ2) {
        if (v1 === v2) {
          // Equal bindus: both reduced to 0
          ekadhipatyaReduced[s1] = 0;
          ekadhipatyaReduced[s2] = 0;
        } else {
          // Unequal bindus: the larger is reduced to equal the smaller (both retain smaller)
          const minV = Math.min(v1, v2);
          ekadhipatyaReduced[s1] = minV;
          ekadhipatyaReduced[s2] = minV;
        }
        return;
      }

      // Case C: One sign is occupied, the other is unoccupied
      const occupiedSign = occ1 ? s1 : s2;
      const emptySign = occ1 ? s2 : s1;
      const vOcc = ekadhipatyaReduced[occupiedSign];
      const vEmpty = ekadhipatyaReduced[emptySign];

      // The occupied sign retains its bindus unconditionally
      if (vEmpty > vOcc) {
        // If empty sign has more bindus, it is reduced to equal the occupied sign
        ekadhipatyaReduced[emptySign] = vOcc;
      } else {
        // If empty sign has fewer or equal bindus, it is reduced to 0
        ekadhipatyaReduced[emptySign] = 0;
      }
    });

    // =========================================================================
    // 3. Classical Shodhya Pinda (BPHS Chapter 71)
    // =========================================================================
    // Rashi Multipliers (Rashi Gunakas):
    // Aries: 7, Taurus: 10, Gemini: 8, Cancer: 4, Leo: 10, Virgo: 6,
    // Libra: 7, Scorpio: 8, Sagittarius: 9, Capricorn: 5, Aquarius: 11, Pisces: 12
    const rashiMultipliers = [7, 10, 8, 4, 10, 6, 7, 8, 9, 5, 11, 12];
    let rashiSum = 0;
    for (let i = 0; i < 12; i++) {
      rashiSum += ekadhipatyaReduced[i] * rashiMultipliers[i];
    }

    // Graha Multipliers (Graha Gunakas):
    // Sun: 5, Moon: 5, Mars: 8, Mercury: 7, Jupiter: 10, Venus: 7, Saturn: 5
    // Derived strictly by multiplying the reduced bindu in OCCUPIED signs by occupant's gunaka.
    const classicalGrahaGunakas: Record<string, number> = {
      Sun: 5,
      Moon: 5,
      Mars: 8,
      Mercury: 7,
      Jupiter: 10,
      Venus: 7,
      Saturn: 5
    };

    let grahaSum = 0;
    const sevenGrahas = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
    sevenGrahas.forEach(gName => {
      const grahaPos = kundali.planets.find(p => p.name === gName);
      if (grahaPos) {
        const occSignIdx = grahaPos.signIndex;
        const reducedBindu = ekadhipatyaReduced[occSignIdx];
        const multiplier = classicalGrahaGunakas[gName] || 0;
        grahaSum += reducedBindu * multiplier;
      }
    });

    results.push({
      planet,
      original,
      trikonaReduced,
      ekadhipatyaReduced,
      shodhita: ekadhipatyaReduced,
      rashiPinda: rashiSum,
      grahaPinda: grahaSum,
      shodhyaPinda: rashiSum + grahaSum
    });
  });

  return results;
}

/**
 * Calculates Reduced Sarvashtakavarga (Samudaya Shodhana) by summing
 * the Ekadhipatya-reduced BAV bindus across all 12 zodiac signs.
 */
export function calculateReducedSarvashtakavarga(shodhanaResults: ShodhanaResult[]): number[] {
  const reducedSav = new Array(12).fill(0);
  for (let s = 0; s < 12; s++) {
    reducedSav[s] = shodhanaResults.reduce((sum, res) => sum + (res.ekadhipatyaReduced[s] || 0), 0);
  }
  return reducedSav;
}

/**
 * Generates Ashtakvarga Predictions across life domains using explicit Bhava resolution
 */
export function generateAshtakvargaPredictions(kundali: CompleteKundali): AshtakvargaPredictionItem[] {
  const { houses, ashtakvarga, planets } = kundali;

  const domains = [
    { category: 'Career', title: 'Professional Growth & Recognition', houseNum: 10 },
    { category: 'Wealth', title: 'Financial Accumulation & Stability', houseNum: 2 },
    { category: 'Business', title: 'Enterprise, Trade & Partnerships', houseNum: 7 },
    { category: 'Property', title: 'Real Estate, Vehicles & Assets', houseNum: 4 },
    { category: 'Marriage', title: 'Marital Harmony & Relationships', houseNum: 7 },
    { category: 'Children', title: 'Progeny, Creativity & Intellect', houseNum: 5 },
    { category: 'Education', title: 'Higher Learning & Wisdom', houseNum: 9 },
    { category: 'Health', title: 'Vitality, Immunity & Resilience', houseNum: 1 },
    { category: 'Travel', title: 'Foreign Travels & Long Journeys', houseNum: 12 },
    { category: 'Spiritual', title: 'Higher Purpose & Inner Liberation', houseNum: 9 }
  ];

  return domains.map(d => {
    const houseInfo = houses.find(h => h.houseNumber === d.houseNum) || houses[0];
    const savScore = getSavByBhava(ashtakvarga, d.houseNum, houses);
    
    let supportLevel: AshtakvargaPredictionItem['supportLevel'] = 'Moderately Supported';
    let confidence = 75;
    if (savScore >= 32) {
      supportLevel = 'Strongly Supported';
      confidence = 90;
    } else if (savScore < 25) {
      supportLevel = 'Requires Caution';
      confidence = 60;
    }

    const lord = houseInfo.signLord;
    const lordPlanet = planets.find(p => p.name === lord);
    const occupantList = houseInfo.planetsInHouse.join(', ') || 'No direct occupants';

    return {
      category: d.category,
      title: d.title,
      houseNumber: d.houseNum,
      sign: houseInfo.sign,
      savScore,
      bavScore: savScore > 30 ? 5 : 4,
      supportLevel,
      confidenceLevel: confidence,
      whyExplanation: {
        houseSav: `House ${d.houseNum} (${houseInfo.sign}) has an SAV score of ${savScore} bindus (${savScore >= 32 ? 'Strong' : savScore >= 28 ? 'Good' : 'Moderate'}).`,
        houseLord: `House lord is ${lord} (${lordPlanet ? `placed in House ${lordPlanet.house} in ${lordPlanet.sign}` : 'N/A'}).`,
        bavDetails: `Occupied by: ${occupantList}. Bhinna Ashtakvarga indicates robust benefic support.`,
        transitNote: `When benefic transits activate House ${d.houseNum}, results manifest smoothly with minimal obstruction.`
      }
    };
  });
}

